import type { Box } from "../camera/framing"

// Their car's own outline, from the segmenter's mask (1 = car), smooth and
// steady enough to draw on the live camera. Pure; unit-tested.
//
//   mask → only inside the detector's box for this car (a guard: the
//          segmenter picks one object, but a miss can spill over) → the
//          region the detector follows (small gaps closed) → its outer edge (Moore tracing: the windows
//          inside don't matter) → fewer points (Ramer–Douglas–Peucker) →
//          96 evenly spaced points → lined up with the last outline and
//          blended into it (no jitter) → a smooth path (Catmull-Rom).
//
// Points are flat [x0, y0, x1, y1, …] arrays.

export type Points = Float32Array

export const CONTOUR = {
  POINTS: 96,
  // Simplify to within this many mask pixels.
  EPSILON: 1,
  // The region must be at least this share of the mask.
  MIN_AREA: 0.01,
  // How much of each new outline goes into the one on screen.
  BLEND: 0.6,
  // Smoothing passes over the evenly spaced points.
  SMOOTH_PASSES: 2,
  // How far past the detector's box the outline may run (share of the box).
  BOUND_PAD: 0.06,
  // A car fills at most this much of its own box (the gaps under the
  // bumpers, round the roof); more is a box, not a car (the point missed it).
  MAX_FILL: 0.92,
  // A new outline this far from the one on screen (outlineGap) replaces it
  // instead of blending in: they turned to another view, or another car.
  JUMP: 0.06,
} as const

// The connected region of car pixels the detector is following: the one
// with the most area where it overlaps `hint` (area alone without one).
// Small gaps (a mirror, a shadow line) are closed. Null when there's none.
export function pickComponent(
  mask: ArrayLike<number>,
  w: number,
  h: number,
  hint: Box | null,
): Uint8Array | null {
  const label = new Int32Array(w * h)
  const stack = new Int32Array(w * h)
  let best = -1
  let bestScore = 0
  let next = 0
  for (let start = 0; start < w * h; start++) {
    if (!mask[start] || label[start]) continue
    const id = ++next
    let top = 0
    stack[top++] = start
    label[start] = id
    let area = 0
    let x0 = w
    let y0 = h
    let x1 = -1
    let y1 = -1
    while (top > 0) {
      const i = stack[--top]
      area++
      const x = i % w
      const y = (i - x) / w
      if (x < x0) x0 = x
      if (x > x1) x1 = x
      if (y < y0) y0 = y
      if (y > y1) y1 = y
      if (x > 0 && mask[i - 1] && !label[i - 1]) (label[i - 1] = id), (stack[top++] = i - 1)
      if (x < w - 1 && mask[i + 1] && !label[i + 1]) (label[i + 1] = id), (stack[top++] = i + 1)
      if (y > 0 && mask[i - w] && !label[i - w]) (label[i - w] = id), (stack[top++] = i - w)
      if (y < h - 1 && mask[i + w] && !label[i + w]) (label[i + w] = id), (stack[top++] = i + w)
    }
    if (area < w * h * CONTOUR.MIN_AREA) continue
    const box = { x: x0 / w, y: y0 / h, w: (x1 + 1 - x0) / w, h: (y1 + 1 - y0) / h }
    const score = hint ? area * (overlap(box, hint) + 0.2) : area
    if (score > bestScore) {
      bestScore = score
      best = id
    }
  }
  if (best < 0) return null
  const region = new Uint8Array(w * h)
  for (let i = 0; i < region.length; i++) region[i] = label[i] === best ? 1 : 0
  return erode(dilate(region, w, h), w, h)
}

function overlap(a: Box, b: Box): number {
  const ix = Math.max(0, Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x))
  const iy = Math.max(0, Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y))
  const inter = ix * iy
  const union = a.w * a.h + b.w * b.h - inter
  return union > 0 ? inter / union : 0
}

function dilate(m: Uint8Array, w: number, h: number): Uint8Array {
  const out = new Uint8Array(m.length)
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let on = 0
      for (let dy = -1; dy <= 1 && !on; dy++) {
        const yy = y + dy
        if (yy < 0 || yy >= h) continue
        for (let dx = -1; dx <= 1; dx++) {
          const xx = x + dx
          if (xx >= 0 && xx < w && m[yy * w + xx]) {
            on = 1
            break
          }
        }
      }
      out[y * w + x] = on
    }
  }
  return out
}

function erode(m: Uint8Array, w: number, h: number): Uint8Array {
  const out = new Uint8Array(m.length)
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let on = 1
      for (let dy = -1; dy <= 1 && on; dy++) {
        const yy = y + dy
        for (let dx = -1; dx <= 1; dx++) {
          const xx = x + dx
          // The picture's edge counts as car: a car cut off stays cut off.
          if (yy >= 0 && yy < h && xx >= 0 && xx < w && !m[yy * w + xx]) {
            on = 0
            break
          }
        }
      }
      out[y * w + x] = on && m[y * w + x] ? 1 : 0
    }
  }
  return out
}

// Neighbours clockwise on screen (y down): E, SE, S, SW, W, NW, N, NE.
const DX = [1, 1, 0, -1, -1, -1, 0, 1]
const DY = [0, 1, 1, 1, 0, -1, -1, -1]
const dirOf = (dx: number, dy: number) => DX.findIndex((x, i) => x === dx && DY[i] === dy)

// The outer edge of a region, as pixel centres in order (Moore-neighbour
// tracing; done when the first step repeats: back at the start, about to
// go to the second pixel again).
export function traceOuter(m: ArrayLike<number>, w: number, h: number): Points {
  let start = -1
  for (let i = 0; i < w * h; i++) {
    if (m[i]) {
      start = i
      break
    }
  }
  if (start < 0) return new Float32Array(0)
  const on = (x: number, y: number) => x >= 0 && y >= 0 && x < w && y < h && !!m[y * w + x]
  const sx = start % w
  const sy = (start - sx) / w
  const out: number[] = [sx + 0.5, sy + 0.5]
  // Raster order: the start's west neighbour is background.
  let back = 4
  let cx = sx
  let cy = sy
  let second = -1
  const limit = 4 * w * h
  for (let step = 0; step < limit; step++) {
    let found = -1
    for (let k = 1; k <= 8; k++) {
      const d = (back + k) % 8
      if (on(cx + DX[d], cy + DY[d])) {
        found = d
        break
      }
    }
    if (found < 0) break
    const nx = cx + DX[found]
    const ny = cy + DY[found]
    const atStart = cx === sx && cy === sy
    if (atStart && step > 0 && ny * w + nx === second) break
    if (step === 0) second = ny * w + nx
    // The last background neighbour looked at, seen from the new pixel.
    const prev = (found + 7) % 8
    back = dirOf(cx + DX[prev] - nx, cy + DY[prev] - ny)
    cx = nx
    cy = ny
    if (cx !== sx || cy !== sy) out.push(cx + 0.5, cy + 0.5)
  }
  return Float32Array.from(out)
}

// Fewer points for a closed outline, none more than `eps` off it.
export function rdp(pts: Points, eps: number = CONTOUR.EPSILON): Points {
  const n = pts.length / 2
  if (n < 4) return pts
  // Split the loop at its two most distant points.
  let far = 0
  let farD = -1
  for (let i = 1; i < n; i++) {
    const d = (pts[2 * i] - pts[0]) ** 2 + (pts[2 * i + 1] - pts[1]) ** 2
    if (d > farD) {
      farD = d
      far = i
    }
  }
  const keep = new Uint8Array(n)
  keep[0] = 1
  keep[far] = 1
  const simplify = (a: number, b: number) => {
    // Indices a..b along the loop (b may wrap past n).
    const ax = pts[2 * (a % n)]
    const ay = pts[2 * (a % n) + 1]
    const bx = pts[2 * (b % n)]
    const by = pts[2 * (b % n) + 1]
    const len = Math.hypot(bx - ax, by - ay) || 1
    let worst = -1
    let worstD = eps
    for (let i = a + 1; i < b; i++) {
      const px = pts[2 * (i % n)]
      const py = pts[2 * (i % n) + 1]
      const d = Math.abs((bx - ax) * (ay - py) - (ax - px) * (by - ay)) / len
      if (d > worstD) {
        worstD = d
        worst = i
      }
    }
    if (worst < 0) return
    keep[worst % n] = 1
    simplify(a, worst)
    simplify(worst, b)
  }
  simplify(0, far)
  simplify(far, n)
  const out: number[] = []
  for (let i = 0; i < n; i++) if (keep[i]) out.push(pts[2 * i], pts[2 * i + 1])
  return Float32Array.from(out)
}

// The same closed outline as `count` evenly spaced points.
export function resample(pts: Points, count: number = CONTOUR.POINTS): Points {
  const n = pts.length / 2
  if (n < 2) return pts
  let total = 0
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n
    total += Math.hypot(pts[2 * j] - pts[2 * i], pts[2 * j + 1] - pts[2 * i + 1])
  }
  const out = new Float32Array(count * 2)
  const step = total / count
  let seg = 0
  let segStart = 0
  let segLen = Math.hypot(pts[2] - pts[0], pts[3] - pts[1])
  for (let k = 0; k < count; k++) {
    const at = k * step
    while (segStart + segLen < at && seg < n - 1) {
      segStart += segLen
      seg++
      const j = (seg + 1) % n
      segLen = Math.hypot(pts[2 * j] - pts[2 * seg], pts[2 * j + 1] - pts[2 * seg + 1])
    }
    const j = (seg + 1) % n
    const f = segLen > 0 ? Math.min(1, (at - segStart) / segLen) : 0
    out[2 * k] = pts[2 * seg] + (pts[2 * j] - pts[2 * seg]) * f
    out[2 * k + 1] = pts[2 * seg + 1] + (pts[2 * j + 1] - pts[2 * seg + 1]) * f
  }
  return out
}

// Signed area (shoelace): the sign says which way round the outline runs.
export function signedArea(pts: Points): number {
  const n = pts.length / 2
  let a = 0
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n
    a += pts[2 * i] * pts[2 * j + 1] - pts[2 * j] * pts[2 * i + 1]
  }
  return a / 2
}

// `next` turned the same way round as `prev`, starting at the point that
// lines it up best (both the same length), so blending moves each point a
// little instead of sliding the whole outline round.
export function alignTo(prev: Points, next: Points): Points {
  const n = next.length / 2
  if (prev.length !== next.length || n === 0) return next
  let src = next
  if (Math.sign(signedArea(prev)) !== Math.sign(signedArea(next))) {
    src = new Float32Array(next.length)
    for (let i = 0; i < n; i++) {
      src[2 * i] = next[2 * (n - 1 - i)]
      src[2 * i + 1] = next[2 * (n - 1 - i) + 1]
    }
  }
  let bestShift = 0
  let bestCost = Infinity
  for (let s = 0; s < n; s++) {
    let cost = 0
    for (let i = 0; i < n && cost < bestCost; i++) {
      const j = (i + s) % n
      cost += (src[2 * j] - prev[2 * i]) ** 2 + (src[2 * j + 1] - prev[2 * i + 1]) ** 2
    }
    if (cost < bestCost) {
      bestCost = cost
      bestShift = s
    }
  }
  const out = new Float32Array(next.length)
  for (let i = 0; i < n; i++) {
    const j = (i + bestShift) % n
    out[2 * i] = src[2 * j]
    out[2 * i + 1] = src[2 * j + 1]
  }
  return out
}

// How far apart two lined-up outlines are: the average point distance, as a
// share of the new outline's size (its box diagonal).
export function outlineGap(prev: Points, next: Points): number {
  const n = next.length / 2
  if (prev.length !== next.length || n === 0) return Infinity
  let sum = 0
  let x0 = Infinity
  let y0 = Infinity
  let x1 = -Infinity
  let y1 = -Infinity
  for (let i = 0; i < n; i++) {
    sum += Math.hypot(next[2 * i] - prev[2 * i], next[2 * i + 1] - prev[2 * i + 1])
    x0 = Math.min(x0, next[2 * i])
    x1 = Math.max(x1, next[2 * i])
    y0 = Math.min(y0, next[2 * i + 1])
    y1 = Math.max(y1, next[2 * i + 1])
  }
  return sum / n / Math.max(1e-6, Math.hypot(x1 - x0, y1 - y0))
}

export function blend(prev: Points, next: Points, alpha: number = CONTOUR.BLEND): Points {
  if (prev.length !== next.length) return next
  const out = new Float32Array(next.length)
  for (let i = 0; i < out.length; i++) out[i] = prev[i] + (next[i] - prev[i]) * alpha
  return out
}

// Moves an outline traced when the car was at `from` to where the detector
// sees it now (`to`): between two traces it follows the car.
export function followBox(pts: Points, from: Box, to: Box): Points {
  if (from.w <= 0 || from.h <= 0) return pts
  const sx = to.w / from.w
  const sy = to.h / from.h
  const out = new Float32Array(pts.length)
  for (let i = 0; i < pts.length; i += 2) {
    out[i] = to.x + (pts[i] - from.x) * sx
    out[i + 1] = to.y + (pts[i + 1] - from.y) * sy
  }
  return out
}

// Irons out pixel-sized wiggles (a shadow line under the car, the mask's
// stair steps) without moving the car's real shape: each point moves part
// of the way toward the middle of its neighbours, a few times.
export function smoothPoints(pts: Points, passes: number = CONTOUR.SMOOTH_PASSES): Points {
  const n = pts.length / 2
  let cur = pts
  for (let k = 0; k < passes; k++) {
    const next = new Float32Array(cur.length)
    for (let i = 0; i < n; i++) {
      const a = (i - 1 + n) % n
      const b = (i + 1) % n
      next[2 * i] = cur[2 * i] * 0.5 + (cur[2 * a] + cur[2 * b]) * 0.25
      next[2 * i + 1] = cur[2 * i + 1] * 0.5 + (cur[2 * a + 1] + cur[2 * b + 1]) * 0.25
    }
    cur = next
  }
  return cur
}

// The car pixels inside `bound` (0–1 of the mask) only.
export function within(mask: ArrayLike<number>, w: number, h: number, bound: Box): Uint8Array {
  const out = new Uint8Array(w * h)
  const x0 = Math.max(0, Math.floor(bound.x * w))
  const x1 = Math.min(w, Math.ceil((bound.x + bound.w) * w))
  const y0 = Math.max(0, Math.floor(bound.y * h))
  const y1 = Math.min(h, Math.ceil((bound.y + bound.h) * h))
  for (let y = y0; y < y1; y++) for (let x = x0; x < x1; x++) out[y * w + x] = mask[y * w + x] ? 1 : 0
  return out
}

// The whole step: the car's outline in 0–1 of the mask, or null. `hint` is
// the detector's box for the car (0–1 of the mask): the outline stays
// inside it, padded by CONTOUR.BOUND_PAD.
export function traceCar(mask: ArrayLike<number>, w: number, h: number, hint: Box | null): Points | null {
  const p = CONTOUR.BOUND_PAD
  const own = hint
    ? within(mask, w, h, { x: hint.x - hint.w * p, y: hint.y - hint.h * p, w: hint.w * (1 + 2 * p), h: hint.h * (1 + 2 * p) })
    : mask
  const region = pickComponent(own, w, h, hint)
  if (!region) return null
  const edge = traceOuter(region, w, h)
  if (edge.length < 8) return null
  const even = smoothPoints(resample(rdp(edge), CONTOUR.POINTS))
  for (let i = 0; i < even.length; i += 2) {
    even[i] /= w
    even[i + 1] /= h
  }
  let x0 = Infinity
  let y0 = Infinity
  let x1 = -Infinity
  let y1 = -Infinity
  for (let i = 0; i < even.length; i += 2) {
    x0 = Math.min(x0, even[i])
    x1 = Math.max(x1, even[i])
    y0 = Math.min(y0, even[i + 1])
    y1 = Math.max(y1, even[i + 1])
  }
  // Box-shaped: the background round a missed point, not a car.
  if (Math.abs(signedArea(even)) >= (x1 - x0) * (y1 - y0) * CONTOUR.MAX_FILL) return null
  return even
}

// A smooth closed SVG path through the points (Catmull-Rom as cubic
// Béziers), mapped by `at` (e.g. 0–1 → screen pixels).
export function toSvgPath(pts: Points, at: (x: number, y: number) => [number, number]): string {
  const n = pts.length / 2
  if (n < 3) return ""
  const p = Array.from({ length: n }, (_, i) => at(pts[2 * i], pts[2 * i + 1]))
  const f = (v: number) => Math.round(v * 10) / 10
  let d = `M${f(p[0][0])} ${f(p[0][1])}`
  for (let i = 0; i < n; i++) {
    const p0 = p[(i - 1 + n) % n]
    const p1 = p[i]
    const p2 = p[(i + 1) % n]
    const p3 = p[(i + 2) % n]
    const c1x = p1[0] + (p2[0] - p0[0]) / 6
    const c1y = p1[1] + (p2[1] - p0[1]) / 6
    const c2x = p2[0] - (p3[0] - p1[0]) / 6
    const c2y = p2[1] - (p3[1] - p1[1]) / 6
    d += `C${f(c1x)} ${f(c1y)} ${f(c2x)} ${f(c2y)} ${f(p2[0])} ${f(p2[1])}`
  }
  return `${d}Z`
}
